<template>
  <v-card
    class="elevation-12"
    :color="isDark ? 'grey darken-4' : 'grey lighten-4'"
    min-height="800"
  >
    <v-toolbar :color="getThemeColor" dark flat>
      <v-btn icon @click="$refs.calendar.prev()">
        <v-icon>mdi-chevron-left</v-icon>
      </v-btn>
      <v-btn icon @click="$refs.calendar.next()">
        <v-icon>mdi-chevron-right</v-icon>
      </v-btn>
      <v-toolbar-title v-if="$refs.calendar">{{
        $refs.calendar.title
      }}</v-toolbar-title>
      <v-spacer></v-spacer>

      <v-menu bottom right>
        <template v-slot:activator="{ on, attrs }">
          <v-btn outlined v-bind="attrs" v-on="on">
            <span>{{ typeLabels[calendarType] }}</span>
            <v-icon right>mdi-menu-down</v-icon>
          </v-btn>
        </template>
        <v-list>
          <v-list-item @click="calendarType = 'day'">
            <v-list-item-title>Day View</v-list-item-title>
          </v-list-item>
          <v-list-item @click="calendarType = 'week'">
            <v-list-item-title>Week View</v-list-item-title>
          </v-list-item>
        </v-list>
      </v-menu>

      <v-btn
        v-if="getAuthenticated"
        fab
        small
        color="white"
        light
        class="ml-4"
        @click="openBookingDialog(null)"
      >
        <v-icon color="primary">mdi-plus</v-icon>
      </v-btn>
    </v-toolbar>

    <v-sheet height="700">
      <v-calendar
        ref="calendar"
        v-model="focus"
        :type="calendarType"
        :events="filteredEvents"
        :event-color="getEventColor"
        :color="getThemeColor"
        @mousedown:event="startDrag"
        @mousedown:time="startTime"
        @mousemove:time="mouseMove"
        @mouseup:time="endDrag"
        @mouseleave.native="cancelDrag"
        @click:event="showEvent"
        @click:date="viewDay"
        @click:more="viewDay"
        @click:time="handleTimeClick"
        :first-interval="8"
        :interval-count="12"
        :interval-height="60"
      >
        <template v-slot:event="{ event }">
          <div class="v-event-draggable px-1">
            <v-icon v-if="event.private" x-small class="mr-1">mdi-lock</v-icon>
            <strong>{{ event.name }}</strong>
          </div>
        </template>
      </v-calendar>
    </v-sheet>

    <v-dialog v-model="dialog" max-width="600px" persistent>
      <v-card v-if="selectedEvent">
        <v-toolbar :color="getEventColor(selectedEvent)" dark flat>
          <v-toolbar-title>{{ selectedEvent.name }}</v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn icon @click="dialog = false"><v-icon>mdi-close</v-icon></v-btn>
        </v-toolbar>

        <v-card-text class="pt-4">
          <div v-if="!getAuthenticated && !selectedEvent.published">
            <v-alert type="info" outlined>This slot is already booked.</v-alert>
          </div>

          <div v-else>
            <v-list two-line>
              <v-list-item>
                <v-list-item-icon
                  ><v-icon>mdi-clock-outline</v-icon></v-list-item-icon
                >
                <v-list-item-content>
                  <v-list-item-title
                    >{{ selectedEvent.start }} -
                    {{ selectedEvent.end }}</v-list-item-title
                  >
                  <v-list-item-subtitle
                    >Appointment Duration</v-list-item-subtitle
                  >
                </v-list-item-content>
              </v-list-item>

              <template v-if="getAuthenticated">
                <v-divider></v-divider>
                <v-subheader>Management & Billing</v-subheader>

                <v-select
                  v-model="selectedEvent.staffId"
                  :items="staffMembers"
                  item-text="name"
                  item-value="id_number"
                  label="Assign Staff Member"
                  prepend-icon="mdi-account"
                  outlined
                  class="mt-2"
                ></v-select>

                <v-select
                  v-model="selectedEvent.rateId"
                  :items="rates"
                  item-text="name"
                  item-value="rate"
                  label="Select Billing Rate"
                  prepend-icon="mdi-cash"
                  outlined
                  return-object
                ></v-select>

                <v-checkbox
                  v-model="selectedEvent.addToInvoice"
                  label="Add to Client Invoice"
                ></v-checkbox>
                <v-select
                  v-if="selectedEvent.addToInvoice"
                  v-model="selectedEvent.invoiceId"
                  :items="invoices"
                  item-text="invoice_number"
                  label="Select Existing Invoice or Create New"
                  outlined
                ></v-select>

                <v-checkbox
                  v-model="selectedEvent.addToPayroll"
                  label="Add to Staff Payroll"
                ></v-checkbox>
              </template>
            </v-list>
          </div>
        </v-card-text>

        <v-card-actions>
          <v-btn v-if="getAuthenticated" color="error" text @click="deleteEvent"
            >Cancel Appointment</v-btn
          >
          <v-spacer></v-spacer>
          <v-btn color="primary" @click="saveManagement">Update</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="bookingDialog" max-width="500px">
      <v-card px-4>
        <v-card-title class="headline" :style="{ color: getThemeColor }"
          >Book Appointment</v-card-title
        >
        <v-card-text>
          <v-text-field
            v-model="newEvent.name"
            label="Your Name / Title"
            outlined
          ></v-text-field>
          <v-row>
            <v-col cols="6">
              <v-text-field
                v-model="newEvent.date"
                label="Date"
                type="date"
                outlined
              ></v-text-field>
            </v-col>
            <v-col cols="6">
              <v-select
                v-model="newEvent.duration"
                :items="durations"
                label="Duration"
                outlined
              ></v-select>
            </v-col>
          </v-row>

          <template v-if="getAuthenticated">
            <v-select
              v-model="newEvent.priority"
              :items="priorities"
              label="Priority"
              outlined
            ></v-select>
            <v-switch
              v-model="newEvent.private"
              label="Private Appointment"
            ></v-switch>
            <v-switch
              v-model="newEvent.published"
              label="Publish to Public"
            ></v-switch>
            <v-switch
              v-model="newEvent.unavailable"
              label="Mark as Unavailable (Block)"
            ></v-switch>
          </template>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="bookingDialog = false">Cancel</v-btn>
          <v-btn :color="getThemeColor" dark @click="submitBooking"
            >Confirm Booking</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script>
import axios from '@/plugins/axios';

export default {
  data: () => ({
    loading: false, // For progress overlays
    focus: "",
    calendarType: "week",
    typeLabels: {
      week: "Weekly View",
      day: "Daily View",
    },
    dialog: false,
    bookingDialog: false,
    selectedEvent: null,
    selectedElement: null,

    // Form Objects
    newEvent: {
      name: "",
      date: "",
      time: "09:00",
      duration: 30,
      priority: "low",
      private: false,
      published: false,
      unavailable: false,
    },

    // Data from API
    events: [],
    staffMembers: [],
    rates: [],
    invoices: [],
    durations: [
      { text: "15 Minutes", value: 15 },
      { text: "30 Minutes", value: 30 },
      { text: "1 Hour", value: 60 },
      { text: "2 Hours", value: 120 },
    ],
    priorities: ["high", "medium", "low"],

    //drag and drop
    dragEvent: null,
  dragStart: null,
  dragTime: null,
  createEvent: null,
  createStart: null,
  }),

  computed: {
    getThemeColor() {
      return this.$store.getters.getThemeColor;
    },
    getAuthenticated() {
      return this.$store.getters.getAuthenticated;
    }, // Toggle for testing
    getCurrency() {
      return this.$store.getters.getCurrency;
    },
    getCurrentUser() {
      return this.$store.getters.getCurrentUser;
    },
    isDark() {
      return false;
    },

    // Core Filtering Logic
    filteredEvents() {
      return this.events.map(event => ({
        ...event,
        color: this.getEventColor(event)
      }));
    },
  },
  async mounted() {
   await this.fetchInitialData();
  },
  methods: {
    async fetchInitialData() {
      this.loading = true;
      try {
        // Fetch everything in parallel
        const [eventsRes] = await Promise.all([
          axios.getData('/appointments',this.getAccessToken),
          //axios.get('/api/staff'), // Assuming you have these endpoints
          //axios.get('/api/rates')
        ]);
        
        this.events = eventsRes.data;
        //this.staffMembers = staffRes.data;
        //this.rates = ratesRes.data;
      } catch (error) {
        console.error("Failed to load calendar data", error);
      } finally {
        this.loading = false;
      }
    },
    async submitBooking() {
      try {
        this.loading = true;
        const response = await axios.postDataWithToken('/auth/appointments', this.newEvent,this.getAccessToken);
        
        // Refresh local events list
        await this.fetchInitialData(); 
        
        this.bookingDialog = false;
        this.resetNewEvent();
      } catch (error) {
        alert("Error saving appointment: " + error.response.data.message);
      } finally {
        this.loading = false;
      }
    },
    async saveManagement() {
      try {
        this.loading = true;
        // Logic maps to the update() function in AppointmentController
        await axios.postDataWithToken(`/auth/appointments/${this.selectedEvent.id}`, {
          staffId: this.selectedEvent.staffId,
          rateId: this.selectedEvent.rateId, // Passes the object or ID
          invoiceId: this.selectedEvent.invoiceId,
          addToInvoice: this.selectedEvent.addToInvoice,
          addToPayroll: this.selectedEvent.addToPayroll
        },this.getAccessToken);

        this.dialog = false;
        await this.fetchInitialData();
      } catch (error) {
        console.error("Update failed", error);
      } finally {
        this.loading = false;
      }
    },
    async deleteEvent() {
      if (!confirm("Are you sure you want to cancel this appointment?")) return;

      try {
        this.loading = true;
        await axios.postDataWithToken(`/auth/appointments/${this.selectedEvent.id}`,{},this.getAccessToken);
        this.events = this.events.filter(e => e.id !== this.selectedEvent.id);
        this.dialog = false;
      } catch (error) {
        console.error("Delete failed", error);
      } finally {
        this.loading = false;
      }
    },
    //UI Helpers
    viewDay({ date }) {
      this.focus = date;
      this.calendarType = "day";
    },
    getEventColor(event) {
      if (event.unavailable) return "grey darken-3";
      if (event.color) return event.color;
      const p = event.priority || "low";
      return p === "high" ? "red" : p === "medium" ? "orange" : "blue";
    },
    showEvent({ nativeEvent, event }) {
      const open = () => {
        this.selectedEvent = event;
        this.selectedElement = nativeEvent.target;
        requestAnimationFrame(() => requestAnimationFrame(() => (this.dialog = true)));
      };
      if (this.dialog) {
        this.dialog = false;
        requestAnimationFrame(() => requestAnimationFrame(() => open()));
      } else {
        open();
      }
      nativeEvent.stopPropagation();
    },
    handleTimeClick(t) {
      this.openBookingDialog(t);
    },
    openBookingDialog(t) {
      this.newEvent.date = t ? t.date : new Date().toISOString().substr(0, 10);
      this.newEvent.time = t ? t.time : "09:00";
      this.bookingDialog = true;
    },
    resetNewEvent() {
      this.newEvent = {
        name: "", date: "", time: "09:00", duration: 30,
        priority: "low", private: false, published: false, unavailable: false,
      };
    },

    //drag and drop methods
    // 1. Capture the event and the initial click time
  startDrag({ event, timed }) {
    if (event && timed) {
      this.dragEvent = event;
      this.dragTime = null;
    }
  },

  // 2. Track the start of a potential new slot
  startTime(t) {
    this.dragStart = t;
  },

  // 3. Update the UI as the mouse moves
  mouseMove(t) {
    if (this.dragEvent && this.dragTime !== t.time) {
      const start = this.dragEvent.start;
      const end = this.dragEvent.end;
      
      // Calculate duration in minutes
      const duration = (new Date(end).getTime() - new Date(start).getTime()) / 60000;
      
      // Temporary UI update (shallow)
      const newStart = t.date + ' ' + t.time;
      const newEnd = this.formatDate(new Date(new Date(newStart).getTime() + duration * 60000));
      
      this.dragEvent.start = newStart;
      this.dragEvent.end = newEnd;
      this.dragTime = t.time;
    }
  },

  // 4. Finalize the move and save to Laravel
  async endDrag() {
    if (!this.dragEvent) return;

    try {
      this.loading = true;
      
      // Sync with our Laravel API
      await axios.postDataWithToken(`/auth/appointments/${this.dragEvent.id}`, {
        start: this.dragEvent.start,
        end: this.dragEvent.end
      },this.getAccessToken);

      // Cleanup
      this.dragEvent = null;
      this.dragTime = null;
    } catch (error) {
      console.error("Reschedule failed", error);
      // Revert if API fails
      await this.fetchInitialData();
    } finally {
      this.loading = false;
    }
  },

  cancelDrag() {
    if (this.dragEvent) {
      this.dragEvent = null;
      this.fetchInitialData(); // Reset to server state
    }
  },
    
    submitBooking() {
      const start = this.newEvent.date + " " + this.newEvent.time;
      // Simple duration calculation
      const endTime = new Date(
        new Date(start).getTime() + this.newEvent.duration * 60000
      );

      const booking = {
        name: this.newEvent.name || "New Appointment",
        start: start,
        end: this.formatDate(endTime),
        priority: this.newEvent.priority,
        private: this.newEvent.private,
        published: this.newEvent.published,
        unavailable: this.newEvent.unavailable,
        createdBy: this.getAuthenticated ? this.getCurrentUser.id : "public",
      };

      this.events.push(booking);
      this.bookingDialog = false;
      this.resetNewEvent();
    },
    saveManagement() {
      // 9. Add to Invoice logic
      if (this.selectedEvent.addToInvoice) {
        console.log("Invoicing Client:", this.selectedEvent.invoiceId);
      }
      // 10. Add to Payroll logic
      if (this.selectedEvent.addToPayroll) {
        console.log("Adding to Payroll for staff:", this.selectedEvent.staffId);
      }
      this.dialog = false;
    },
    deleteEvent() {
      this.events = this.events.filter((e) => e !== this.selectedEvent);
      this.dialog = false;
    },
    formatDate(date) {
      const d = new Date(date);
      return (
        d.getFullYear() +
        "-" +
        ("0" + (d.getMonth() + 1)).slice(-2) +
        "-" +
        ("0" + d.getDate()).slice(-2) +
        " " +
        ("0" + d.getHours()).slice(-2) +
        ":" +
        ("0" + d.getMinutes()).slice(-2)
      );
    },
    
  },
};
</script>

<style scoped>
.v-event-draggable {
  padding-left: 6px;
}
</style>
